// components/Dashboard.jsx - Updated main component
import React, { useState, useEffect } from "react";
import { Container, Grid } from "@mui/material";
import {
  LocalCarWash, LocalGasStation, DirectionsCar, ReportProblem
} from '@mui/icons-material';
import BadgeIcon from '@mui/icons-material/Badge';

import { useDashboardData } from '../hooks/useDashboardData';
import {
  processVehicleData,
  processFuelData,
  processTopVehicles,
  processInfractionData
} from '../utils/dataProcessors';
import { getAvailableYears, filterByDateRange } from '../utils/dateUtils';

import DashboardCard from './DashboardCard';
import ChartCard from './ChartCard';
import FilterControls from './FilterControls';

// Import all chart components
import {
  VehicleStateChart,
  FuelConsumptionChart,
  TopVehiclesChart,
  FuelByTypeChart,
  InfractionsByDriverChart,
  InfractionsByTypeChart,
  VehiclesByBrandChart
} from './charts';

const themeColor = "#152d51";

const Dashboard = () => {
  const { rawData, loading, error } = useDashboardData();

  // Processed data state
  const [dashboardData, setDashboardData] = useState({
    vehicleStates: [],
    vehiclesByBrand: [],
    fuelByType: [],
    monthlyFuelData: [],
    infractionData: [],
    infractionsByType: [],
    topFuelVehicles: [],
    topMaintenanceVehicles: [],
    topInfractionVehicles: [],
    totals: { vehicles: 0, fuel: 0, drivers: 0, infractions: 0 }
  });

  // Filter states
  const [filters, setFilters] = useState({
    fuel: { type: 'all', year: new Date().getFullYear() },
    maintenance: { type: 'all', year: new Date().getFullYear() },
    infractions: { type: 'all', year: new Date().getFullYear() }
  });

  // Process data when raw data changes
  useEffect(() => {
    if (!rawData.vehiculos || !rawData.vehiculos.length) return;

    const { vehicleStates, vehiclesByBrand } = processVehicleData(rawData.vehiculos);
    const { fuelByType, monthlyFuelData } = processFuelData(rawData.combustibles);
    const { infractionData, infractionsByType } = processInfractionData(rawData.infracciones);

    const totals = {
      vehicles: rawData.vehiculos.length,
      fuel: monthlyFuelData.reduce((sum, f) => sum + f.litros, 0),
      drivers: rawData.conductores.length,
      infractions: rawData.infracciones.length
    };

    setDashboardData(prev => ({
      ...prev,
      vehicleStates,
      vehiclesByBrand,
      fuelByType,
      monthlyFuelData,
      infractionData,
      infractionsByType,
      totals
    }));
  }, [rawData]);

  // Update top vehicles when filters change
  useEffect(() => {
    if (!rawData.vehiculos.length) return;

    // Process fuel data with filters
    const filteredFuelData = filterByDateRange(
      rawData.combustibles,
      'fecha_y_hora',
      filters.fuel
    );
    const topFuelVehicles = processTopVehicles(
      filteredFuelData,
      rawData.vehiculos,
      'fuel'
    );

    // Process maintenance data with filters
    const filteredMaintenanceData = filterByDateRange(
      rawData.mantenimientoData || [],
      'fecha_requerimiento',
      filters.maintenance
    );
    const topMaintenanceVehicles = processTopVehicles(
      filteredMaintenanceData,
      rawData.vehiculos,
      'maintenance'
    );

    // Process infraction data with filters
    const filteredInfractionData = filterByDateRange(
      rawData.infracciones,
      'fecha',
      filters.infractions
    );
    const topInfractionVehicles = processTopVehicles(
      filteredInfractionData,
      rawData.vehiculos,
      'infractions'
    );

    setDashboardData(prev => ({
      ...prev,
      topFuelVehicles,
      topMaintenanceVehicles,
      topInfractionVehicles
    }));
  }, [filters, rawData]);

  const updateFilter = (type, newFilter) => {
    setFilters(prev => ({
      ...prev,
      [type]: { ...prev[type], type: newFilter }
    }));
  };

  const updateFilterYear = (type, year) => {
    setFilters(prev => ({
      ...prev,
      [type]: { ...prev[type], year }
    }));
  };

  const availableFuelYears = getAvailableYears(rawData.combustibles, 'fecha_y_hora');
  const availableMaintenanceYears = getAvailableYears(rawData.mantenimientoData || [], 'fecha_requerimiento');
  const availableInfractionYears = getAvailableYears(rawData.infracciones, 'fecha');

  const [filtersInitialized, setFiltersInitialized] = useState(false);

  useEffect(() => {
    if (
      filtersInitialized ||
      !rawData.vehiculos ||
      rawData.vehiculos.length === 0
    ) return;

    const latestFuelYear = availableFuelYears.length
      ? Math.max(...availableFuelYears)
      : new Date().getFullYear();
    const latestMaintenanceYear = availableMaintenanceYears.length
      ? Math.max(...availableMaintenanceYears)
      : new Date().getFullYear();
    const latestInfractionYear = availableInfractionYears.length
      ? Math.max(...availableInfractionYears)
      : new Date().getFullYear();

    setFilters({
      fuel: { type: 'all', year: latestFuelYear },
      maintenance: { type: 'all', year: latestMaintenanceYear },
      infractions: { type: 'all', year: latestInfractionYear }
    });

    setFiltersInitialized(true);
  }, [rawData, availableFuelYears, availableMaintenanceYears, availableInfractionYears, filtersInitialized]);


  if (loading) return <div>Cargando...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Container maxWidth="lg" sx={{ mt: 0, mb: 6 }}>
      <Grid container spacing={4}>
        {/* Dashboard Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Vehículos"
            value={dashboardData.totals.vehicles}
            icon={LocalCarWash}
            color={themeColor}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Consumo"
            value={`${dashboardData.totals.fuel.toFixed(0)} L`}
            icon={LocalGasStation}
            color={themeColor}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Conductores"
            value={dashboardData.totals.drivers}
            icon={BadgeIcon}
            color={themeColor}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <DashboardCard
            title="Infracciones"
            value={dashboardData.totals.infractions}
            icon={ReportProblem}
            color={themeColor}
          />
        </Grid>

        {/* Charts Row 1 */}
        <Grid item xs={12} md={6} lg={6}>
          <ChartCard title="Estado de Vehículos" color={themeColor}>
            <VehicleStateChart data={dashboardData.vehicleStates} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          <ChartCard title="Vehículos por Marca" color={themeColor}>
            <VehiclesByBrandChart data={dashboardData.vehiclesByBrand} />
          </ChartCard>
        </Grid>

        {/* Fuel Consumption Chart */}
        <Grid item xs={12} md={12} lg={12}>
          <ChartCard title="Consumo y Costo de los últimos meses" color={themeColor}>
            <FuelConsumptionChart data={dashboardData.monthlyFuelData} />
          </ChartCard>
        </Grid>

        {/* Top Fuel Vehicles */}
        <Grid item xs={12} md={12} lg={12}>
          <ChartCard title="Vehículos con Más Consumo" color={themeColor}>
            <FilterControls
              filter={filters.fuel.type}
              onFilterChange={(filter) => updateFilter('fuel', filter)}
              selectedYear={filters.fuel.year}
              onYearChange={(year) => updateFilterYear('fuel', year)}
              availableYears={availableFuelYears}
            />
            <TopVehiclesChart
              data={dashboardData.topFuelVehicles}
              dataKey="litros"
              color="#0F172A"
            />
          </ChartCard>
        </Grid>

        {/* Top Maintenance Vehicles */}
        <Grid item xs={12} md={12} lg={12}>
          <ChartCard title="Vehículos con más Mantenimiento" color={themeColor}>
            <FilterControls
              filter={filters.maintenance.type}
              onFilterChange={(filter) => updateFilter('maintenance', filter)}
              selectedYear={filters.maintenance.year}
              onYearChange={(year) => updateFilterYear('maintenance', year)}
              availableYears={availableMaintenanceYears}
            />
            <TopVehiclesChart
              data={dashboardData.topMaintenanceVehicles}
              dataKey="mantenimientos"
              color="#8de546"
            />
          </ChartCard>
        </Grid>

        {/* Top Infraction Vehicles */}
        <Grid item xs={12} md={12} lg={12}>
          <ChartCard title="Vehículos con Más Infracciones" color={themeColor}>
            <FilterControls
              filter={filters.infractions.type}
              onFilterChange={(filter) => updateFilter('infractions', filter)}
              selectedYear={filters.infractions.year}
              onYearChange={(year) => updateFilterYear('infractions', year)}
              availableYears={availableInfractionYears}
            />
            <TopVehiclesChart
              data={dashboardData.topInfractionVehicles}
              dataKey="infracciones"
              color="#FF6B6B"
            />
          </ChartCard>
        </Grid>

        {/* Fuel and Infractions Charts */}
        <Grid item xs={12} md={6} lg={6}>
          <ChartCard title="Consumo por Tipo de Producto" color={themeColor}>
            <FuelByTypeChart data={dashboardData.fuelByType} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6} lg={6}>
          <ChartCard title="Infracciones de Tránsito" color={themeColor}>
            <InfractionsByDriverChart data={dashboardData.infractionData} />
          </ChartCard>
        </Grid>

        {/* Infractions by Type */}
        <Grid item xs={12} md={6} lg={6}>
          <ChartCard title="Infracciones por Tipo" color={themeColor}>
            <InfractionsByTypeChart data={dashboardData.infractionsByType} />
          </ChartCard>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;